import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useSearchParams, Link } from "react-router-dom";
import { API, fmtPrice } from "@/lib/site";
import { CheckCircle2, Copy, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";

const MAX_POLLS = 12;
const INTERVAL = 2000;

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [order, setOrder] = useState(null);
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          const o = await axios.get(`${API}/orders/by-session/${sessionId}`);
          setOrder(o.data);
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(res.data.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) {
        // keep trying
      }
      pollsRef.current += 1;
      if (pollsRef.current >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, INTERVAL);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const copyOrder = () => {
    navigator.clipboard.writeText(order.order_number);
    toast.success("Bestellnummer kopiert");
  };

  return (
    <div data-testid="payment-success-page" className="relative z-[2] min-h-screen grid place-items-center px-6 py-24">
      <div className="w-full max-w-lg">
        {state === "checking" && (
          <div data-testid="payment-checking" className="text-center">
            <Loader2 size={40} className="animate-spin text-[#e2ff00] mx-auto mb-6" />
            <h1 className="font-display font-bold text-2xl">Zahlung wird geprüft…</h1>
            <p className="mt-2 text-[#8b8b93] text-sm">Einen Moment, wir bestätigen deine Bestellung.</p>
          </div>
        )}

        {state === "paid" && order && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-white/10 bg-[#0f0f11] p-8 text-center rounded-lg">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 14 }} className="mx-auto w-16 h-16 grid place-items-center rounded-full bg-[#e2ff00]/10 border border-[#e2ff00]/40 mb-6">
              <CheckCircle2 size={32} className="text-[#e2ff00]" />
            </motion.div>
            <h1 className="font-display font-extrabold text-3xl">Bezahlt & bestätigt</h1>
            <p className="mt-2 text-sm text-[#8b8b93]">
              <span className="font-display font-bold text-white">{order.pack_name}</span> wird an den Epic-Account{" "}
              <span data-testid="success-epic" className="text-[#e2ff00] font-mono">{order.epic_name}</span> geliefert.
            </p>

            <div className="mt-6 border border-white/10 rounded-lg divide-y divide-white/10 text-left">
              <button onClick={copyOrder} className="w-full flex items-center justify-between px-5 py-4 group">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.2em] text-[#8b8b93]">BESTELLNUMMER</div>
                  <div data-testid="success-order-number" className="font-mono text-lg text-white">{order.order_number}</div>
                </div>
                <Copy size={16} className="text-[#8b8b93] group-hover:text-white transition-colors" />
              </button>
              <div className="flex items-center justify-between px-5 py-4">
                <span className="font-mono text-[10px] tracking-[0.2em] text-[#8b8b93]">GESAMT</span>
                <span className="font-display font-bold">{fmtPrice(order.price)}</span>
              </div>
            </div>

            <p className="mt-5 text-xs text-[#8b8b93] leading-relaxed">
              Unser Team meldet sich in Kürze, um die verifizierte Lieferung an deinen Epic-Account abzuschließen.
            </p>
            <Link data-testid="success-home" to="/" className="mt-6 inline-block w-full bg-white text-black font-mono text-sm uppercase tracking-[0.2em] py-3.5 rounded-full hover:bg-[#e2ff00] transition-colors">
              Zurück zum Shop
            </Link>
          </motion.div>
        )}

        {(state === "timeout" || state === "error") && (
          <div data-testid="payment-issue" className="border border-white/10 bg-[#0f0f11] p-8 text-center rounded-lg">
            <XCircle size={40} className="text-[#ff0055] mx-auto mb-6" />
            <h1 className="font-display font-bold text-2xl">
              {state === "timeout" ? "Zahlung wird noch verarbeitet" : "Etwas ist schiefgelaufen"}
            </h1>
            <p className="mt-2 text-[#8b8b93] text-sm">
              {state === "timeout"
                ? "Deine Zahlung dauert etwas länger. Prüfe deine E-Mails oder lade die Seite neu."
                : "Wir konnten deine Bestellung nicht bestätigen. Bitte versuche es erneut."}
            </p>
            <Link to="/" className="mt-6 inline-block bg-white text-black font-mono text-sm uppercase tracking-[0.2em] px-6 py-3 rounded-full hover:bg-[#e2ff00] transition-colors">
              Zurück zum Shop
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
